import { apiBaseUrl } from './baseUrl';
import { retryAfterSeconds } from './retryAfter';

// ─────────────────────────────────────────────────────────────────────
// ACCEPTING A CLUB'S INVITATION (#1069, the landing #1061 left open).
//
// The other half of `memberInvitation.ts`, which SENDS one (the club's side).
//
// The client for `/auth/member/invitation/start` and `/accept`
// (`apps/api/src/routes/memberInvitationAccept.ts`). The e-mail a club sends
// links to `/?invitation=<token>`; the link proves DELIVERY, a code mailed by
// `start` proves the MAILBOX, and only both together attach the person to the
// club's roster player (#197 Q4: a club rosters an existing player only with
// the player's consent — this is where that consent is given).
//
// ── WHY NOT `apiFetch` ────────────────────────────────────────────
//
// The same reason as `accountSignIn.ts`: this is the request that OBTAINS a
// credential, so it must not carry whatever half-dead one the surface holds.
// `credentials: 'include'` so the browser applies the `spm_` cookie `accept`
// sets — the gate then asks `/member/me` (`memberSession.restore()`), which is
// the same path a reload takes, rather than adopting a session built here.
//
// ── ONE FLAT SHAPE, LIKE `AccountSignInOutcome` ───────────────────
//
// `code: string | null` — null means it worked. The repository compiles with
// `strict: false`, under which a boolean discriminant does not narrow.
// ─────────────────────────────────────────────────────────────────────

/** Codes this client produces itself, so a caller never has to guess. */
export const INVITATION_UNREACHABLE = 'unreachable';
export const INVITATION_NO_API = 'no_api';

/** What `start` says the invitation is for. Shown so the person knows what they accept. */
export interface InvitationContext {
  clubName: string;
  playerName: string;
  /** Whether accepting also makes them a member, or only links them to the player. */
  grantsMembership: boolean;
}

export interface InvitationStartOutcome {
  /** A machine code from the API, or null when the code was mailed. Never a sentence. */
  code: string | null;
  context: InvitationContext | null;
  /** On `too_many_attempts` only: the server's wait in whole seconds, 0 when unreadable. */
  retryAfterSeconds?: number;
}

export interface InvitationAcceptOutcome {
  /** A machine code from the API, or null when the invitation was accepted. */
  code: string | null;
  /**
   * TRUE ON A REFUSED ATTACHMENT. The code was right, so the address is proven
   * and stays proven — what was refused is this invitation (the player was
   * claimed by somebody else, this account already has a player, another club
   * rostered them meanwhile). The screen must say so rather than «wrong code».
   */
  verified: boolean;
  retryAfterSeconds?: number;
}

/** The token from the address bar, or null. Only the shape the link carries is read. */
export function invitationTokenFromLocation(
  search: string = typeof window !== 'undefined' ? window.location.search : '',
): string | null {
  try {
    const token = new URLSearchParams(search).get('invitation')?.trim() ?? '';
    return token === '' ? null : token;
  } catch {
    return null;
  }
}

/**
 * Remove the token from the address bar, keeping everything else.
 *
 * It is a credential for this one acceptance: once the screen is done with it —
 * accepted, or abandoned — it must not survive in the URL to be bookmarked,
 * shared or replayed by a reload into a dead invitation.
 */
export function clearInvitationFromLocation(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('invitation')) return;
    url.searchParams.delete('invitation');
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    /* no window, or a sandbox without history — nothing to clear */
  }
}

/**
 * POST, or say why there was no answer: no API configured, or no network. A
 * dropped connection is not a refusal, so it gets its own code rather than
 * implying anything about the invitation.
 */
async function post(
  path: string,
  body: unknown,
): Promise<Response | typeof INVITATION_NO_API | typeof INVITATION_UNREACHABLE> {
  const base = apiBaseUrl();
  if (!base) return INVITATION_NO_API;
  try {
    return await fetch(base + path, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  } catch {
    return INVITATION_UNREACHABLE;
  }
}

const errorOf = (body: { error?: unknown } | null): string =>
  typeof body?.error === 'string' ? body.error : 'unknown_error';

/** Open the invitation: the API mails a code to the invited address. */
export async function startInvitation(token: string): Promise<InvitationStartOutcome> {
  const res = await post('/auth/member/invitation/start', { token });
  if (typeof res === 'string') return { code: res, context: null };
  const body = (await res.json().catch(() => null)) as {
    error?: unknown;
    club?: { name?: unknown };
    playerName?: unknown;
    grantsMembership?: unknown;
  } | null;
  if (!res.ok) {
    const code = errorOf(body);
    return code === 'too_many_attempts'
      ? { code, context: null, retryAfterSeconds: retryAfterSeconds(res) }
      : { code, context: null };
  }
  const clubName = typeof body?.club?.name === 'string' ? body.club.name : '';
  const playerName = typeof body?.playerName === 'string' ? body.playerName : '';
  // A 200 without the context is not a usable answer: the person would be
  // accepting something the screen cannot name.
  if (!clubName || !playerName) return { code: 'unknown_error', context: null };
  return {
    code: null,
    context: { clubName, playerName, grantsMembership: body?.grantsMembership === true },
  };
}

/** Spend the code. On success the `spm_` cookie is set; the caller restores the session. */
export async function acceptInvitation(
  token: string,
  code: string,
): Promise<InvitationAcceptOutcome> {
  const res = await post('/auth/member/invitation/accept', { token, code });
  if (typeof res === 'string') return { code: res, verified: false };
  const body = (await res.json().catch(() => null)) as {
    ok?: unknown;
    error?: unknown;
    verified?: unknown;
  } | null;
  if (!res.ok) {
    const code = errorOf(body);
    return code === 'too_many_attempts'
      ? { code, verified: false, retryAfterSeconds: retryAfterSeconds(res) }
      : { code, verified: body?.verified === true };
  }
  // A 200 that does not say ok is not an acceptance.
  if (body?.ok !== true) return { code: 'unknown_error', verified: false };
  return { code: null, verified: true };
}
